/**
 * Bridge Pattern - Firestore Data Provider Implementation
 * * Features:
 * - Dual-Level Pathing: Supports entities at both Organization and Project levels.
 * - Hybrid Pattern: External data cleaner + Internal generic helpers.
 * - Soft Delete: Uses 'tombstone' markers instead of hard deletion.
 * - Explicit Typing: All async methods explicitly return Promise<T>.
 */

import { IDataProvider } from "./IDataProvider";
import {
  AppState,
  Organization,
  User,
  Team,
  Project,
  CostItem,
  Requirement,
  ProjectNdaSignature,
  AccountingRequirement,
  AccountingMilestone,
  CapitalLedgerEntry,
  Task,
  Epic,
  Story,
  Sprint,
  Retrospective,
  Report,
  Comment,
  Notification,
  UserRequest,
  StandupNote,
  PinnedMessage,
  NewsFeedPost,
  NewsFeedComment,
} from "../../types";

import {
  getFirestore,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  Firestore,
} from "firebase/firestore";
import { getOrgFromUrl } from "../../utils/urlToOrg";
import { generateId } from "../../utils/idGenerator";

// --- DESIGN: Immutable ID Architecture ---
// All entities use Firestore doc.id as the immutable primary key.
// Each entity has two display-related fields:
//
// 1. id: Immutable unique identifier (set at creation, cannot be edited).
//    - Same as Firestore doc.id for project sub-collections
//    - Most common human reference; never changes
//
// 2. idLabel: User-editable optional display label.
//    - Used for routing and display when provided
//    - Allows custom names (e.g., "FEATURE-42", "BUG-001")
//    - If empty, UI defaults to last 3 characters of doc.id
//    - Can be searched in UI
//
// Flow:
// - On create: id is set to doc.id (immutable); idLabel is optional
// - On display: Show idLabel if provided, otherwise show last 3 chars of id
// - On query: Search by id field in most cases
// - On routing: Use idLabel when available for user-friendly URLs
// --- END DESIGN ---

// --- UTILITY: DATA PREPARATION ---

const prepareData = (data: any, id: string) => {
  return Object.fromEntries(
    Object.entries({
      ...data,
      id,
      tombstone: false, 
      updatedAt: serverTimestamp(),
    }).filter(([_, v]) => v !== undefined)
  );
};

// --- HELPER INTERFACES ---

interface FlexibleEntity {
  id?: string;
  organizationId: string;
  projectId?: string; // Presence determines if Project or Org level
}

interface UserOrganizationAccess {
  organizationId: string;
  projectIds: Set<string>;
  hasExplicitProjects: boolean;
}

type CommentEntityType = 'task' | 'epic' | 'story' | 'project' | 'sprint' | 'retrospective' | 'report';

type ProjectSubCollectionItem = {
  id: string;
  organizationId?: string;
  projectId?: string;
};

const PROJECT_SUBCOLLECTION_COLLECTIONS = new Set([
  'workItems',
  'epics',
  'stories',
  'sprints',
  'retrospectives',
  'reports',
  'userRequests',
  'standupNotes',
  'costItems',
  'requirements',
  'ndaSignatures',
  'accountingRequirements',
  'accountingMilestones',
  'capitalLedgerEntries',
]);

export class FirestoreDataProvider implements IDataProvider {
  private db: Firestore = getFirestore();
  private ready: boolean = true;

  // =========================================================
  // INTERNAL HELPERS (REUSE LOGIC)
  // =========================================================
  
  /**
   * Resolves path based on whether projectId is present in the entity.
   */
  private getPath(entity: FlexibleEntity, collectionName: string): string {
    return entity.projectId 
      ? `organizations/${entity.organizationId}/projects/${entity.projectId}/${collectionName}`
      : `organizations/${entity.organizationId}/${collectionName}`;
  }

  private async saveEntity<T extends FlexibleEntity>(
    collectionName: string,
    entity: T,
    idPrefix: string
  ): Promise<void> {
    const id = entity.id ?? generateId(idPrefix);
    const basePath = this.getPath(entity, collectionName);
    const docRef = doc(this.db, basePath, id);
    
    await setDoc(docRef, prepareData(entity, id), { merge: true });
  }

  private normalizeEntity(doc: any, collectionName: string): any {
    // Simply ensure id is always present for consistency
    return doc;
  }

  private async getEntities<T>(path: string): Promise<T[]> {
    const ref = collection(this.db, path);
    const q = query(ref, where("tombstone", "!=", true));
    const snap = await getDocs(q);
    // Extract collection name from path (last segment)
    const collectionName = path.split('/').pop() || '';
    return snap.docs.map(d => {
      const entity = { id: d.id, ...d.data() };
      return this.normalizeEntity(entity, collectionName) as T;
    });
  }

  private async tombstoneEntity(path: string): Promise<void> {
    const docRef = doc(this.db, path);
    await updateDoc(docRef, {
      tombstone: true,
      deletedAt: serverTimestamp()
    });
  }

  private getCollectionNameForEntityType(entityType: CommentEntityType): string {
    switch (entityType) {
      case 'task':
        return 'workItems';
      case 'epic':
        return 'epics';
      case 'story':
        return 'stories';
      case 'project':
        return 'projects';
      case 'sprint':
        return 'sprints';
      case 'retrospective':
        return 'retrospectives';
      case 'report':
        return 'reports';
    }
  }

  private async getEntityDocByType(entityType: CommentEntityType, entityId: string) {
    const collectionName = this.getCollectionNameForEntityType(entityType);
    return this.getEntityDocByCollectionName(collectionName, entityId);
  }

  private async getEntityDocByCollectionName(collectionName: string, entityId: string) {
    let entityDoc: any;

    // Preferred path: indexed collectionGroup query on `id`. May fail if the
    // single-field collection-group index is missing (FAILED_PRECONDITION) — in
    // that case we silently fall back to a full scan below.
    try {
      const entityQuery = query(collectionGroup(this.db, collectionName), where('id', '==', entityId));
      const snap = await getDocs(entityQuery);
      entityDoc = snap.docs.find(docSnap => !docSnap.data()?.tombstone);
    } catch (err: any) {
      const code = err?.code || '';
      const message = err?.message || '';
      const isMissingIndex =
        code === 'failed-precondition' || /requires an? .* index/i.test(message);
      if (!isMissingIndex) {
        throw err;
      }
      console.warn(
        `[Firestore] Missing collectionGroup index on '${collectionName}.id'. Falling back to full scan.`,
      );
    }

    // Fallback for legacy records missing an explicit `id` field, OR when the
    // indexed query above was skipped due to a missing index.
    if (!entityDoc) {
      const fallbackSnap = await getDocs(collectionGroup(this.db, collectionName));
      const isProjectSubcollection = PROJECT_SUBCOLLECTION_COLLECTIONS.has(collectionName);
      entityDoc = fallbackSnap.docs.find((docSnap) => {
        if (docSnap.data()?.tombstone) {
          return false;
        }

        if (docSnap.data()?.id === entityId) {
          return true;
        }

        if (isProjectSubcollection) {
          return docSnap.id === entityId || docSnap.data()?.documentId === entityId;
        }

        return docSnap.id === entityId;
      });
    }

    if (!entityDoc) {
      throw new Error(`[Firestore] ${collectionName} entity not found: ${entityId}`);
    }

    return entityDoc;
  }

  private async assertUniqueEntityIdForScope(
    collectionName: string,
    nextId: string,
    currentEntityDoc: any
  ): Promise<void> {
    const normalizedId = String(nextId || '').trim();
    if (!normalizedId) {
      return;
    }

    const currentData = currentEntityDoc.data() as Record<string, unknown>;
    const currentOrgId = String(currentData.organizationId || '');
    const currentProjectId = String(currentData.projectId || '');
    const isProjectSubcollection = PROJECT_SUBCOLLECTION_COLLECTIONS.has(collectionName);

    const duplicateQuery = query(collectionGroup(this.db, collectionName), where('id', '==', normalizedId));
    const duplicateSnap = await getDocs(duplicateQuery);

    const hasDuplicate = duplicateSnap.docs.some((docSnap) => {
      if (docSnap.ref.path === currentEntityDoc.ref.path) {
        return false;
      }

      const data = docSnap.data() as Record<string, unknown>;
      if (data.tombstone) {
        return false;
      }

      const candidateOrgId = String(data.organizationId || '');
      const candidateProjectId = String(data.projectId || '');

      if (isProjectSubcollection) {
        return candidateOrgId === currentOrgId && candidateProjectId === currentProjectId;
      }

      return candidateOrgId === currentOrgId;
    });

    if (hasDuplicate) {
      throw new Error(`Duplicate id \"${normalizedId}\" already exists.`);
    }
  }

  private getCollectionNameForPinnedEntity(entityType: 'project' | 'sprint' | 'task'): string {
    switch (entityType) {
      case 'task':
        return 'workItems';
      case 'sprint':
        return 'sprints';
      case 'project':
        return 'projects';
    }
  }

  private normalizeIdentifier(value: unknown): string | null {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return null;
  }

  private getUserOrganizationAccess(user: User): Map<string, UserOrganizationAccess> {
    const accessByOrg = new Map<string, UserOrganizationAccess>();

    const ensureOrgAccess = (organizationId: string): UserOrganizationAccess => {
      const existing = accessByOrg.get(organizationId);
      if (existing) {
        return existing;
      }

      const created: UserOrganizationAccess = {
        organizationId,
        projectIds: new Set<string>(),
        hasExplicitProjects: false,
      };

      accessByOrg.set(organizationId, created);
      return created;
    };

    const addProjectIds = (access: UserOrganizationAccess, projectList: unknown): void => {
      if (!Array.isArray(projectList)) {
        return;
      }

      access.hasExplicitProjects = true;

      projectList.forEach((projectId) => {
        const normalizedProjectId = this.normalizeIdentifier(projectId);
        if (normalizedProjectId) {
          access.projectIds.add(normalizedProjectId);
        }
      });
    };

    const primaryOrgId = this.normalizeIdentifier(user.organizationId);
    if (primaryOrgId) {
      const access = ensureOrgAccess(primaryOrgId);
      addProjectIds(access, (user as any).projects);
    }

    const organizationEntries = Array.isArray(user.organizations) ? user.organizations : [];
    organizationEntries.forEach((entry: unknown) => {
      if (typeof entry === 'string' || typeof entry === 'number') {
        const organizationId = this.normalizeIdentifier(entry);
        if (organizationId) {
          ensureOrgAccess(organizationId);
        }
        return;
      }

      if (!this.isPlainObject(entry)) {
        return;
      }

      const organizationId = this.normalizeIdentifier(
        (entry as any).organizationId ?? (entry as any).id
      );

      if (!organizationId) {
        return;
      }

      const access = ensureOrgAccess(organizationId);
      addProjectIds(access, (entry as any).projects ?? (entry as any).projectIds);
    });

    return accessByOrg;
  }

  private canUserAccessProject(
    user: User,
    project: Project,
    orgAccess?: UserOrganizationAccess
  ): boolean {
    if (user.role === 'admin') {
      return true;
    }

    const userIdentityCandidates = new Set<string>();
    if (typeof user.id === 'string' && user.id) {
      userIdentityCandidates.add(user.id);
    }
    if (typeof user.email === 'string' && user.email) {
      userIdentityCandidates.add(user.email);
    }

    const isListedInOrgAccess = !!orgAccess?.projectIds.has(project.id);
    const isProjectTeamMember = (project.teamMembers || []).some((member) =>
      userIdentityCandidates.has(String(member.userId || ''))
    );
    const isExplicitlyAllowed = (project.allowedUserIds || []).some((allowedUserId) =>
      userIdentityCandidates.has(String(allowedUserId || ''))
    );

    if (isListedInOrgAccess || isProjectTeamMember || isExplicitlyAllowed) {
      return true;
    }

    // If a user has an explicit projects list for an org, deny all projects not in the allow-list.
    if (orgAccess?.hasExplicitProjects) {
      return false;
    }

    // Legacy users without explicit per-project lists still require explicit project membership.
    return false;
  }

  private filterProjectScopedEntities<T>(
    entities: T[],
    allowedProjectIds: Set<string>
  ): T[] {
    return entities.filter((entity) => {
      const projectId = (entity as any)?.projectId;
      if (!projectId) {
        return true;
      }

      return allowedProjectIds.has(projectId);
    });
  }

  private uniqueById<T extends { id?: string }>(entities: T[]): T[] {
    const seen = new Set<string>();

    return entities.filter((entity) => {
      if (!entity.id) {
        return true;
      }

      if (seen.has(entity.id)) {
        return false;
      }

      seen.add(entity.id);
      return true;
    });
  }

  private async getUsersForOrganizations(
    organizationIds: string[],
    currentUser?: User
  ): Promise<User[]> {
    if (!organizationIds.length) {
      return currentUser ? [currentUser] : [];
    }

    const allowedOrgIdSet = new Set(organizationIds);
    const users = await this.getEntities<User>('users');

    const filteredUsers = users.filter((user) => {
      const userOrgAccess = this.getUserOrganizationAccess(user);
      return Array.from(userOrgAccess.keys()).some((orgId) => allowedOrgIdSet.has(orgId));
    });

    if (currentUser && !filteredUsers.some((user) => user.id === currentUser.id)) {
      filteredUsers.push(currentUser);
    }

    return this.uniqueById(filteredUsers);
  }

  private isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') {
      return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
  }

  private stripUndefinedDeep<T>(value: T): T {
    if (value === undefined) {
      return undefined as T;
    }

    if (Array.isArray(value)) {
      const cleanedArray = value
        .map(item => this.stripUndefinedDeep(item))
        .filter(item => item !== undefined);
      return cleanedArray as T;
    }

    if (this.isPlainObject(value)) {
      const cleanedEntries = Object.entries(value)
        .map(([key, entryValue]) => [key, this.stripUndefinedDeep(entryValue)] as const)
        .filter(([, entryValue]) => entryValue !== undefined);

      return Object.fromEntries(cleanedEntries) as T;
    }

    return value;
  }

  private getCleanUpdateData(updates: Record<string, unknown>): Record<string, unknown> {
    const withTimestamp = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    return this.stripUndefinedDeep(withTimestamp) as Record<string, unknown>;
  }

  private async getEntitiesFromCollectionGroup<T>(
    collectionName: string,
    filters: Array<{ field: string; operator?: '==' | 'array-contains'; value: unknown }> = []
  ): Promise<T[]> {
    const constraints = filters.map(({ field, operator = '==', value }) => where(field, operator, value));
    const groupRef = collectionGroup(this.db, collectionName);
    const groupQuery = constraints.length ? query(groupRef, ...constraints) : query(groupRef);
    const snap = await getDocs(groupQuery);

    return snap.docs
      .map(docSnap => {
        const entity = { id: docSnap.id, ...docSnap.data() } as any;
        return this.normalizeEntity(entity, collectionName);
      })
      .filter(entity => !entity.tombstone) as T[];
  }

  private getProjectSubcollectionPath(orgId: string, projectId: string, collectionName: string): string {
    return `organizations/${orgId}/projects/${projectId}/${collectionName}`;
  }

  private async replaceProjectSubcollectionItems<T extends ProjectSubCollectionItem>(
    orgId: string,
    projectId: string,
    collectionName: string,
    items: T[]
  ): Promise<void> {
    const path = this.getProjectSubcollectionPath(orgId, projectId, collectionName);
    const existingItems = await this.getEntities<T>(path);
    const nextIds = new Set((items || []).map((item) => item.id));

    // Write all items by their id (which is the Firestore doc.id)
    await Promise.all(
      (items || []).map((item) => {
        const itemWithScope = {
          ...item,
          organizationId: item.organizationId || orgId,
          projectId: item.projectId || projectId,
        };
        return setDoc(
          doc(this.db, path, item.id),
          prepareData(itemWithScope, item.id),
          { merge: true }
        );
      })
    );

    // Soft-delete items that were removed
    const removedItems = existingItems.filter((item) => !nextIds.has(item.id));
    await Promise.all(
      removedItems.map((item) =>
        updateDoc(doc(this.db, path, item.id), {
          tombstone: true,
          deletedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      )
    );
  }


  private async hydrateProjectCollections(project: Project, orgId?: string): Promise<Project> {
    const resolvedOrgId = orgId || project.organizationId;
    if (!resolvedOrgId) {
      return {
        ...project,
        costs: [],
        requirements: [],
        ndaSignatures: [],
        accountingRequirements: [],
        accountingMilestones: [],
        capitalLedgerEntries: [],
      };
    }

    const [
      costs,
      requirements,
      ndaSignatures,
      accountingRequirements,
      accountingMilestones,
      capitalLedgerEntries,
    ] = await Promise.all([
      this.getProjectCostItems(resolvedOrgId, project.id),
      this.getProjectRequirements(resolvedOrgId, project.id),
      this.getProjectNdaSignatures(resolvedOrgId, project.id),
      this.getProjectAccountingRequirements(resolvedOrgId, project.id),
      this.getProjectAccountingMilestones(resolvedOrgId, project.id),
      this.getProjectCapitalLedgerEntries(resolvedOrgId, project.id),
    ]);

    return {
      ...project,
      organizationId: resolvedOrgId,
      costs,
      requirements,
      ndaSignatures,
      accountingRequirements,
      accountingMilestones,
      capitalLedgerEntries,
    };
  }

  private async updateEntityByCollectionName(
    collectionName: string,
    entityId: string,
    updates: Record<string, unknown>
  ): Promise<void> {
    const entityDoc = await this.getEntityDocByCollectionName(collectionName, entityId);
    if (typeof updates.id === 'string') {
      await this.assertUniqueEntityIdForScope(collectionName, updates.id, entityDoc);
    }
    await updateDoc(entityDoc.ref, this.getCleanUpdateData(updates));
  }

  private async deleteEntityByCollectionName(collectionName: string, entityId: string): Promise<void> {
    const entityDoc = await this.getEntityDocByCollectionName(collectionName, entityId);
    await updateDoc(entityDoc.ref, {
      tombstone: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  // =========================================================
  // STATE MANAGEMENT
  // =========================================================

  async loadState(userEmail: string): Promise<AppState> {
    try {
      const userSnap = await getDoc(doc(this.db, "users", userEmail));
      if (!userSnap.exists() || userSnap.data()?.tombstone) {
        throw new Error(`User profile not found or deleted: ${userEmail}`);
      }

      const currentUser = { id: userSnap.id, ...userSnap.data() } as User;
      const userOrgAccess = this.getUserOrganizationAccess(currentUser);
      const orgIds = Array.from(userOrgAccess.keys());

      if (!orgIds.length) return this.getEmptyState(currentUser);

      const organizations = (await Promise.all(orgIds.map(id => this.getOrganizationById(id))))
        .filter((o): o is Organization => o !== null);

      const projects: Project[] = [];
      const teams: Team[] = [];
      const tasks: Task[] = [];
      const epics: Epic[] = [];
      const sprints: Sprint[] = [];
      const retrospectives: Retrospective[] = [];
      const reports: Report[] = [];
      const standupNotes: StandupNote[] = [];
      const userRequests: UserRequest[] = [];
      const newsFeedPosts: NewsFeedPost[] = [];

      await Promise.all(organizations.map(async (org) => {
        const orgAccess = userOrgAccess.get(org.id);

        // 1. Load Org-Level flexible collections
        const [orgSprintsRaw, orgRetrosRaw, orgReportsRaw, orgNotesRaw, orgRequestsRaw, orgProjectsRaw, orgTeamsRaw, orgFeedPostsRaw] = await Promise.all([
          this.getEntities<Sprint>(`organizations/${org.id}/sprints`),
          this.getEntities<Retrospective>(`organizations/${org.id}/retrospectives`),
          this.getEntities<Report>(`organizations/${org.id}/reports`),
          this.getEntities<StandupNote>(`organizations/${org.id}/standupNotes`),
          this.getEntities<UserRequest>(`organizations/${org.id}/userRequests`),
          this.getEntities<Project>(`organizations/${org.id}/projects`),
          this.getEntities<Team>(`organizations/${org.id}/teams`),
          this.getEntities<NewsFeedPost>(`organizations/${org.id}/newsFeedPosts`),
        ]);

        const allowedOrgProjects = orgProjectsRaw.filter((project) =>
          this.canUserAccessProject(currentUser, project, orgAccess)
        );
        const orgProjects = await Promise.all(
          allowedOrgProjects.map((project) => this.hydrateProjectCollections(project, org.id))
        );

        const allowedProjectIds = new Set(orgProjects.map((project) => project.id));

        const orgSprints = this.filterProjectScopedEntities(orgSprintsRaw, allowedProjectIds);
        const orgRetros = this.filterProjectScopedEntities(orgRetrosRaw, allowedProjectIds);
        const orgReports = this.filterProjectScopedEntities(orgReportsRaw, allowedProjectIds);
        const orgNotes = this.filterProjectScopedEntities(orgNotesRaw, allowedProjectIds);
        const orgRequests = this.filterProjectScopedEntities(orgRequestsRaw, allowedProjectIds);

        teams.push(...orgTeamsRaw);
        sprints.push(...orgSprints);
        retrospectives.push(...orgRetros);
        reports.push(...orgReports);
        standupNotes.push(...orgNotes);
        userRequests.push(...orgRequests);
        projects.push(...orgProjects);
        newsFeedPosts.push(...orgFeedPostsRaw);

        // 2. Load Project-Level collections
        await Promise.all(orgProjects.map(async (project) => {
          const path = `organizations/${org.id}/projects/${project.id}`;
          // console.log(`🔎 Querying path: ${path}/workItems`);
          const [pTasks, pEpics, pSprints, pRetros, pReports, pNotes, pRequests, pTeams, pFeedPosts] = await Promise.all([
            this.getEntities<Task>(`${path}/workItems`),
            this.getEntities<Epic>(`${path}/epics`),
            this.getEntities<Sprint>(`${path}/sprints`),
            this.getEntities<Retrospective>(`${path}/retrospectives`),
            this.getEntities<Report>(`${path}/reports`),
            this.getEntities<StandupNote>(`${path}/standupNotes`),
            this.getEntities<UserRequest>(`${path}/userRequests`),
            this.getEntities<Team>(`${path}/teams`),
            this.getEntities<NewsFeedPost>(`${path}/newsFeedPosts`),
          ]);

          teams.push(...pTeams);
          tasks.push(...pTasks);
          epics.push(...pEpics);
          sprints.push(...pSprints);
          retrospectives.push(...pRetros);
          reports.push(...pReports);
          standupNotes.push(...pNotes);
          userRequests.push(...pRequests);
          newsFeedPosts.push(...pFeedPosts);
        }));
      }));

      const orgIdFromUrl = getOrgFromUrl();
      const currentOrganization = organizations.find(o => o.id === orgIdFromUrl) || organizations[0] || null;

      // console.log("[FirestoreDataProvider]  currentOrganization: ", currentOrganization);
      // console.log("[FirestoreDataProvider]  projects: ", projects);
      // console.log("[FirestoreDataProvider]  tasks: ", tasks);

      return {
        organizations,
        currentOrganization,
        users: await this.getUsersForOrganizations(orgIds, currentUser),
        currentUser,
        projects: this.uniqueById(projects),
        teams: this.uniqueById(teams),
        tasks: this.uniqueById(tasks),
        epics: this.uniqueById(epics),
        sprints: this.uniqueById(sprints),
        retrospectives: this.uniqueById(retrospectives),
        reports: this.uniqueById(reports),
        standupNotes: this.uniqueById(standupNotes),
        userRequests: this.uniqueById(userRequests),
        newsFeedPosts: this.uniqueById(newsFeedPosts),
        stories: [], 
        reminders: [],
        changeLogs: [],
        attachments: [],
        notifications: await this.getNotifications(currentUser.id),
      };
    } catch (error) {
      console.error("[Firestore] loadState failed:", error);
      throw error;
    }
  }

  private getEmptyState(currentUser?: User): AppState {
    return { 
      organizations: [], users: [], projects: [], tasks: [], 
      sprints: [], epics: [], notifications: [], retrospectives: [],
      reports: [], standupNotes: [], userRequests: [], newsFeedPosts: [],
      teams: [], reminders: [], changeLogs: [], attachments: [],
      currentUser: currentUser || ({ id: '', name: '' } as User)
    } as any;
  }

  // =========================================================
  // ORGANIZATIONS, USERS & PROJECTS
  // =========================================================

  async getOrganizations(): Promise<Organization[]> {
    return this.getEntities<Organization>('organizations');
  }

  async getOrganizationById(orgId: string): Promise<Organization | null> {
    const snap = await getDoc(doc(this.db, 'organizations', orgId));
    return (snap.exists() && !snap.data()?.tombstone)
      ? ({ id: snap.id, ...snap.data() } as Organization)
      : null;
  }

  async addOrganization(org: Organization): Promise<void> {
    await setDoc(doc(this.db, 'organizations', org.id), prepareData(org, org.id), { merge: true });
  }

  async updateOrganization(orgId: string, updates: Partial<Organization>): Promise<void> {
    await updateDoc(doc(this.db, 'organizations', orgId), this.getCleanUpdateData(updates as Record<string, unknown>));
  }

  async deleteOrganization(orgId: string): Promise<void> {
    await updateDoc(doc(this.db, 'organizations', orgId), {
      tombstone: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async getUsers(orgId: string = ''): Promise<User[]> {
    const users = await this.getEntities<User>('users');
    return orgId ? users.filter(user => user.organizationId === orgId) : users;
  }

  async getUserById(userId: string): Promise<User | null> {
    const snap = await getDoc(doc(this.db, 'users', userId));
    return (snap.exists() && !snap.data()?.tombstone)
      ? ({ id: snap.id, ...snap.data() } as User)
      : null;
  }

  async addUser(user: User): Promise<void> {
    await setDoc(doc(this.db, 'users', user.id), prepareData(user, user.id), { merge: true });
  }

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    await updateDoc(doc(this.db, 'users', userId), this.getCleanUpdateData(updates as Record<string, unknown>));
  }

  async deleteUser(userId: string): Promise<void> {
    await updateDoc(doc(this.db, 'users', userId), {
      tombstone: true,
      deletedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  async getUsersByOrganization(orgId: string): Promise<User[]> {
    return this.getUsers(orgId);
  }

  async getProjects(orgId: string): Promise<Project[]> {
    const projects = await this.getEntities<Project>(`organizations/${orgId}/projects`);
    return Promise.all(projects.map((project) => this.hydrateProjectCollections(project, orgId)));
  }

  async getProjectById(projectId: string): Promise<Project | null> {
    try {
      const projectDoc = await this.getEntityDocByCollectionName('projects', projectId);
      const project = { id: projectDoc.id, ...projectDoc.data() } as Project;
      return this.hydrateProjectCollections(project, project.organizationId);
    } catch {
      return null;
    }
  }

  async addProject(orgId: string, project: Project): Promise<void> {
    const {
      costs,
      requirements,
      ndaSignatures,
      accountingRequirements,
      accountingMilestones,
      capitalLedgerEntries,
      ...projectDoc
    } = project as Project & {
      costs?: CostItem[];
      requirements?: Requirement[];
      ndaSignatures?: ProjectNdaSignature[];
      accountingRequirements?: AccountingRequirement[];
      accountingMilestones?: AccountingMilestone[];
      capitalLedgerEntries?: CapitalLedgerEntry[];
    };

    const projectRef = doc(this.db, `organizations/${orgId}/projects`, project.id);
    await Promise.all([
      setDoc(projectRef, prepareData({ ...projectDoc, organizationId: orgId }, project.id), { merge: true }),
      updateDoc(doc(this.db, 'organizations', orgId), { projects: arrayUnion(project.id) }),
    ]);

    await Promise.all([
      this.setProjectCostItems(orgId, project.id, costs || []),
      this.setProjectRequirements(orgId, project.id, requirements || []),
      this.setProjectNdaSignatures(orgId, project.id, ndaSignatures || []),
      this.setProjectAccountingRequirements(orgId, project.id, accountingRequirements || []),
      this.setProjectAccountingMilestones(orgId, project.id, accountingMilestones || []),
      this.setProjectCapitalLedgerEntries(orgId, project.id, capitalLedgerEntries || []),
    ]);
  }

  async updateProject(orgId: string, projectId: string, updates: Partial<Project>): Promise<void> {
    const {
      costs: _ignoredCosts,
      requirements: _ignoredRequirements,
      ndaSignatures: _ignoredNdaSignatures,
      accountingRequirements: _ignoredAccountingRequirements,
      accountingMilestones: _ignoredAccountingMilestones,
      capitalLedgerEntries: _ignoredCapitalLedgerEntries,
      ...projectUpdates
    } = updates as Partial<Project> & {
      costs?: CostItem[];
      requirements?: Requirement[];
      ndaSignatures?: ProjectNdaSignature[];
      accountingRequirements?: AccountingRequirement[];
      accountingMilestones?: AccountingMilestone[];
      capitalLedgerEntries?: CapitalLedgerEntry[];
    };

    await this.updateEntityByCollectionName('projects', projectId, projectUpdates as Record<string, unknown>);
  }

  async deleteProject(orgId: string, projectId: string): Promise<void> {
    await this.deleteEntityByCollectionName('projects', projectId);
    await updateDoc(doc(this.db, 'organizations', orgId), { projects: arrayRemove(projectId) });
  }

  async getProjectsByOrganization(orgId: string): Promise<Project[]> {
    return this.getProjects(orgId);
  }

  async getProjectCostItems(orgId: string, projectId: string): Promise<CostItem[]> {
    return this.getEntities<CostItem>(
      this.getProjectSubcollectionPath(orgId, projectId, 'costItems')
    );
  }

  async setProjectCostItems(orgId: string, projectId: string, items: CostItem[]): Promise<void> {
    await this.replaceProjectSubcollectionItems<CostItem & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'costItems',
      (items || []).map((item) => ({
        ...item,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getProjectRequirements(orgId: string, projectId: string): Promise<Requirement[]> {
    return this.getEntities<Requirement>(
      this.getProjectSubcollectionPath(orgId, projectId, 'requirements')
    );
  }

  async setProjectRequirements(
    orgId: string,
    projectId: string,
    requirements: Requirement[]
  ): Promise<void> {
    await this.replaceProjectSubcollectionItems<Requirement & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'requirements',
      (requirements || []).map((requirement) => ({
        ...requirement,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getProjectNdaSignatures(
    orgId: string,
    projectId: string
  ): Promise<ProjectNdaSignature[]> {
    return this.getEntities<ProjectNdaSignature>(
      this.getProjectSubcollectionPath(orgId, projectId, 'ndaSignatures')
    );
  }

  async setProjectNdaSignatures(
    orgId: string,
    projectId: string,
    signatures: ProjectNdaSignature[]
  ): Promise<void> {
    await this.replaceProjectSubcollectionItems<ProjectNdaSignature & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'ndaSignatures',
      (signatures || []).map((signature) => ({
        ...signature,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getProjectAccountingRequirements(orgId: string, projectId: string): Promise<AccountingRequirement[]> {
    return this.getEntities<AccountingRequirement>(
      this.getProjectSubcollectionPath(orgId, projectId, 'accountingRequirements')
    );
  }

  async setProjectAccountingRequirements(
    orgId: string,
    projectId: string,
    requirements: AccountingRequirement[]
  ): Promise<void> {
    await this.replaceProjectSubcollectionItems<AccountingRequirement & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'accountingRequirements',
      (requirements || []).map((item) => ({
        ...item,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getProjectAccountingMilestones(orgId: string, projectId: string): Promise<AccountingMilestone[]> {
    return this.getEntities<AccountingMilestone>(
      this.getProjectSubcollectionPath(orgId, projectId, 'accountingMilestones')
    );
  }

  async setProjectAccountingMilestones(
    orgId: string,
    projectId: string,
    milestones: AccountingMilestone[]
  ): Promise<void> {
    await this.replaceProjectSubcollectionItems<AccountingMilestone & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'accountingMilestones',
      (milestones || []).map((item) => ({
        ...item,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getProjectCapitalLedgerEntries(orgId: string, projectId: string): Promise<CapitalLedgerEntry[]> {
    return this.getEntities<CapitalLedgerEntry>(
      this.getProjectSubcollectionPath(orgId, projectId, 'capitalLedgerEntries')
    );
  }

  async setProjectCapitalLedgerEntries(
    orgId: string,
    projectId: string,
    entries: CapitalLedgerEntry[]
  ): Promise<void> {
    await this.replaceProjectSubcollectionItems<CapitalLedgerEntry & ProjectSubCollectionItem>(
      orgId,
      projectId,
      'capitalLedgerEntries',
      (entries || []).map((item) => ({
        ...item,
        organizationId: orgId,
        projectId,
      }))
    );
  }

  async getTasks(orgId: string): Promise<Task[]> {
    return this.getEntitiesFromCollectionGroup<Task>('workItems', [{ field: 'organizationId', value: orgId }]);
  }

  async getTaskById(taskId: string): Promise<Task | null> {
    try {
      const taskDoc = await this.getEntityDocByCollectionName('workItems', taskId);
      return { id: taskDoc.id, ...taskDoc.data() } as Task;
    } catch {
      return null;
    }
  }

  async getTasksByProject(projectId: string): Promise<Task[]> {
    return this.getEntitiesFromCollectionGroup<Task>('workItems', [{ field: 'projectId', value: projectId }]);
  }

  async getTasksByEpic(epicId: string): Promise<Task[]> {
    return this.getEntitiesFromCollectionGroup<Task>('workItems', [{ field: 'epicId', value: epicId }]);
  }

  async getTasksBySprint(sprintId: string): Promise<Task[]> {
    const sprint = await this.getSprintById(sprintId);
    if (!sprint || !sprint.taskIds?.length) {
      return [];
    }

    const tasks = await Promise.all((sprint.taskIds || []).map(id => this.getTaskById(id)));
    return tasks.filter((task): task is Task => task !== null);
  }

  async getTasksByAssignee(userId: string): Promise<Task[]> {
    return this.getEntitiesFromCollectionGroup<Task>('workItems', [{ field: 'assigneeIds', operator: 'array-contains', value: userId }]);
  }

  async addTask(task: Task): Promise<void> {
    return this.saveEntity('workItems', task, 'task');
  }

  async updateTask(orgId: string, taskId: string, updates: Partial<Task>): Promise<void> {
    await this.updateEntityByCollectionName('workItems', taskId, updates as Record<string, unknown>);
  }

  async deleteTask(orgId: string, taskId: string): Promise<void> {
    await this.deleteEntityByCollectionName('workItems', taskId);
  }

  async getEpics(orgId: string): Promise<Epic[]> {
    return this.getEntitiesFromCollectionGroup<Epic>('epics', [{ field: 'organizationId', value: orgId }]);
  }

  async getEpicById(epicId: string): Promise<Epic | null> {
    try {
      const epicDoc = await this.getEntityDocByCollectionName('epics', epicId);
      return { id: epicDoc.id, ...epicDoc.data() } as Epic;
    } catch {
      return null;
    }
  }

  async getEpicsByProject(projectId: string): Promise<Epic[]> {
    return this.getEntitiesFromCollectionGroup<Epic>('epics', [{ field: 'projectId', value: projectId }]);
  }

  async addEpic(epic: Epic): Promise<void> {
    return this.saveEntity('epics', epic, 'epic');
  }

  async updateEpic(orgId: string, epicId: string, updates: Partial<Epic>): Promise<void> {
    await this.updateEntityByCollectionName('epics', epicId, updates as Record<string, unknown>);
  }

  async deleteEpic(orgId: string, epicId: string): Promise<void> {
    await this.deleteEntityByCollectionName('epics', epicId);
  }

  async getStories(orgId: string): Promise<Story[]> {
    return this.getEntitiesFromCollectionGroup<Story>('stories', [{ field: 'organizationId', value: orgId }]);
  }

  async getStoryById(storyId: string): Promise<Story | null> {
    try {
      const storyDoc = await this.getEntityDocByCollectionName('stories', storyId);
      return { id: storyDoc.id, ...storyDoc.data() } as Story;
    } catch {
      return null;
    }
  }

  async getStoriesByProject(projectId: string): Promise<Story[]> {
    return this.getEntitiesFromCollectionGroup<Story>('stories', [{ field: 'projectId', value: projectId }]);
  }

  async addStory(story: Story): Promise<void> {
    return this.saveEntity('stories', story, 'story');
  }

  async updateStory(orgId: string, storyId: string, updates: Partial<Story>): Promise<void> {
    await this.updateEntityByCollectionName('stories', storyId, updates as Record<string, unknown>);
  }

  async deleteStory(orgId: string, storyId: string): Promise<void> {
    await this.deleteEntityByCollectionName('stories', storyId);
  }

  async getSprints(orgId: string): Promise<Sprint[]> {
    return this.getEntitiesFromCollectionGroup<Sprint>('sprints', [{ field: 'organizationId', value: orgId }]);
  }

  async getSprintById(sprintId: string): Promise<Sprint | null> {
    try {
      const sprintDoc = await this.getEntityDocByCollectionName('sprints', sprintId);
      return { id: sprintDoc.id, ...sprintDoc.data() } as Sprint;
    } catch {
      return null;
    }
  }

  async getSprintsByProject(projectId: string): Promise<Sprint[]> {
    return this.getEntitiesFromCollectionGroup<Sprint>('sprints', [{ field: 'projectId', value: projectId }]);
  }

  async addSprint(item: Sprint): Promise<void> {
    console.log("[FirestoreDataProvider] Adding sprint: ", item);
    return this.saveEntity('sprints', item, 'sprint');
  }

  async updateSprint(orgId: string, sprintId: string, updates: Partial<Sprint>): Promise<void> {
    await this.updateEntityByCollectionName('sprints', sprintId, updates as Record<string, unknown>);
  }

  async deleteSprint(orgId: string, sprintId: string): Promise<void> {
    await this.deleteEntityByCollectionName('sprints', sprintId);
  }

  async getRetrospectives(orgId: string): Promise<Retrospective[]> {
    return this.getEntitiesFromCollectionGroup<Retrospective>('retrospectives', [{ field: 'organizationId', value: orgId }]);
  }

  async getRetrospectiveById(retroId: string): Promise<Retrospective | null> {
    try {
      const retroDoc = await this.getEntityDocByCollectionName('retrospectives', retroId);
      return { id: retroDoc.id, ...retroDoc.data() } as Retrospective;
    } catch {
      return null;
    }
  }

  async getRetrospectivesByProject(projectId: string): Promise<Retrospective[]> {
    return this.getEntitiesFromCollectionGroup<Retrospective>('retrospectives', [{ field: 'projectId', value: projectId }]);
  }

  async addRetrospective(item: Retrospective): Promise<void> {
    return this.saveEntity('retrospectives', item, 'retro');
  }

  async updateRetrospective(orgId: string, retroId: string, updates: Partial<Retrospective>): Promise<void> {
    await this.updateEntityByCollectionName('retrospectives', retroId, updates as Record<string, unknown>);
  }

  async deleteRetrospective(orgId: string, retroId: string): Promise<void> {
    await this.deleteEntityByCollectionName('retrospectives', retroId);
  }

  async getReports(orgId: string): Promise<Report[]> {
    return this.getEntitiesFromCollectionGroup<Report>('reports', [{ field: 'organizationId', value: orgId }]);
  }

  async getReportById(reportId: string): Promise<Report | null> {
    try {
      const reportDoc = await this.getEntityDocByCollectionName('reports', reportId);
      return { id: reportDoc.id, ...reportDoc.data() } as Report;
    } catch {
      return null;
    }
  }

  async getReportsByProject(projectId: string): Promise<Report[]> {
    return this.getEntitiesFromCollectionGroup<Report>('reports', [{ field: 'projectId', value: projectId }]);
  }

  async addReport(item: Report): Promise<void> {
    return this.saveEntity('reports', item, 'report');
  }

  async updateReport(orgId: string, reportId: string, updates: Partial<Report>): Promise<void> {
    await this.updateEntityByCollectionName('reports', reportId, updates as Record<string, unknown>);
  }

  async deleteReport(orgId: string, reportId: string): Promise<void> {
    await this.deleteEntityByCollectionName('reports', reportId);
  }

  async getUserRequests(orgId: string): Promise<UserRequest[]> {
    const requests = await this.getEntitiesFromCollectionGroup<UserRequest>('userRequests');
    return requests.filter((request: any) => !request.organizationId || request.organizationId === orgId);
  }

  async getUserRequestsByTask(taskId: string): Promise<UserRequest[]> {
    return this.getEntitiesFromCollectionGroup<UserRequest>('userRequests', [{ field: 'taskId', value: taskId }]);
  }

  async addUserRequest(item: UserRequest): Promise<void> {
    const organizationId = (item as any).organizationId || getOrgFromUrl();
    if (!organizationId) {
      console.warn('[Firestore] addUserRequest skipped: missing organizationId', item);
      return;
    }

    await this.saveEntity('userRequests', { ...(item as any), organizationId }, 'req');
  }

  async updateUserRequest(requestId: string, updates: Partial<UserRequest>): Promise<void> {
    await this.updateEntityByCollectionName('userRequests', requestId, updates as Record<string, unknown>);
  }

  async deleteUserRequest(requestId: string): Promise<void> {
    await this.deleteEntityByCollectionName('userRequests', requestId);
  }

  async getStandupNotes(orgId: string): Promise<StandupNote[]> {
    return this.getEntitiesFromCollectionGroup<StandupNote>('standupNotes', [{ field: 'organizationId', value: orgId }]);
  }

  async getStandupNotesByUser(userId: string): Promise<StandupNote[]> {
    return this.getEntitiesFromCollectionGroup<StandupNote>('standupNotes', [{ field: 'authorId', value: userId }]);
  }

  async getStandupNotesBySprint(sprintId: string): Promise<StandupNote[]> {
    return this.getEntitiesFromCollectionGroup<StandupNote>('standupNotes', [{ field: 'sprintId', value: sprintId }]);
  }

  async addStandupNote(item: StandupNote): Promise<void> {
    return this.saveEntity('standupNotes', item, 'note');
  }

  async updateStandupNote(noteId: string, updates: Partial<StandupNote>): Promise<void> {
    await this.updateEntityByCollectionName('standupNotes', noteId, updates as Record<string, unknown>);
  }

  async deleteStandupNote(noteId: string): Promise<void> {
    await this.deleteEntityByCollectionName('standupNotes', noteId);
  }

  async getNotifications(userId: string): Promise<Notification[]> {
    return this.getEntities<Notification>(`users/${userId}/notifications`);
  }

  async addNotification(notification: Notification): Promise<void> {
    await setDoc(
      doc(this.db, `users/${notification.userId}/notifications`, notification.id),
      prepareData(notification, notification.id),
      { merge: true }
    );
  }

  async updateNotification(notificationId: string, updates: Partial<Notification>): Promise<void> {
    await this.updateEntityByCollectionName('notifications', notificationId, updates as Record<string, unknown>);
  }

  async deleteNotification(notificationId: string): Promise<void> {
    await this.deleteEntityByCollectionName('notifications', notificationId);
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    await this.updateNotification(notificationId, { read: true } as Partial<Notification>);
  }

  async addPinnedMessage(
    entityType: 'project' | 'sprint' | 'task',
    entityId: string,
    message: PinnedMessage
  ): Promise<void> {
    const collectionName = this.getCollectionNameForPinnedEntity(entityType);
    const entityDoc = await this.getEntityDocByCollectionName(collectionName, entityId);
    const data = entityDoc.data() as { pinnedMessages?: PinnedMessage[] };
    const pinnedMessages = [...(data.pinnedMessages || []), message];

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ pinnedMessages }));
  }

  async updatePinnedMessage(
    entityType: 'project' | 'sprint' | 'task',
    entityId: string,
    messageId: string,
    updates: Partial<PinnedMessage>
  ): Promise<void> {
    const collectionName = this.getCollectionNameForPinnedEntity(entityType);
    const entityDoc = await this.getEntityDocByCollectionName(collectionName, entityId);
    const data = entityDoc.data() as { pinnedMessages?: PinnedMessage[] };
    const pinnedMessages = (data.pinnedMessages || []).map(message =>
      message.id === messageId ? { ...message, ...updates } : message
    );

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ pinnedMessages }));
  }

  async deletePinnedMessage(
    entityType: 'project' | 'sprint' | 'task',
    entityId: string,
    messageId: string
  ): Promise<void> {
    const collectionName = this.getCollectionNameForPinnedEntity(entityType);
    const entityDoc = await this.getEntityDocByCollectionName(collectionName, entityId);
    const data = entityDoc.data() as { pinnedMessages?: PinnedMessage[] };
    const pinnedMessages = (data.pinnedMessages || []).filter(message => message.id !== messageId);

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ pinnedMessages }));
  }

  // =========================================================
  // COMMENTS
  // =========================================================

  async addCommentToEntity(
    entityType: CommentEntityType,
    entityId: string,
    comment: Comment
  ): Promise<void> {
    const entityDoc = await this.getEntityDocByType(entityType, entityId);
    const data = entityDoc.data() as { comments?: Comment[] };
    const comments = [...(data.comments || []), comment];

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ comments }));
  }

  async updateCommentInEntity(
    entityType: CommentEntityType,
    entityId: string,
    commentId: string,
    updates: Partial<Comment>
  ): Promise<void> {
    const entityDoc = await this.getEntityDocByType(entityType, entityId);
    const data = entityDoc.data() as { comments?: Comment[] };
    const nextUpdates = {
      ...updates,
      updatedAt: new Date().toISOString(),
      ...(Object.prototype.hasOwnProperty.call(updates, 'content') ? { edited: true } : {}),
    };

    const comments = (data.comments || []).map(comment =>
      comment.id === commentId ? { ...comment, ...nextUpdates } : comment
    );

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ comments }));
  }

  async deleteCommentFromEntity(
    entityType: CommentEntityType,
    entityId: string,
    commentId: string
  ): Promise<void> {
    const entityDoc = await this.getEntityDocByType(entityType, entityId);
    const data = entityDoc.data() as { comments?: Comment[] };
    const comments = (data.comments || []).filter(comment =>
      comment.id !== commentId && comment.parentCommentId !== commentId
    );

    await updateDoc(entityDoc.ref, this.getCleanUpdateData({ comments }));
  }

  // =========================================================
  // NEWS FEED OPERATIONS
  // =========================================================

  private getNewsFeedPath(orgId: string, projectId?: string): string {
    return projectId
      ? this.getProjectSubcollectionPath(orgId, projectId, 'newsFeedPosts')
      : `organizations/${orgId}/newsFeedPosts`;
  }

  async getOrgNewsFeedPosts(orgId: string): Promise<NewsFeedPost[]> {
    return this.getEntities<NewsFeedPost>(`organizations/${orgId}/newsFeedPosts`);
  }

  async getProjectNewsFeedPosts(orgId: string, projectId: string): Promise<NewsFeedPost[]> {
    return this.getEntities<NewsFeedPost>(
      this.getProjectSubcollectionPath(orgId, projectId, 'newsFeedPosts')
    );
  }

  async addNewsFeedPost(post: NewsFeedPost): Promise<void> {
    const path = this.getNewsFeedPath(post.organizationId, post.projectId);
    const id = post.id || generateId('feed');
    await setDoc(doc(this.db, path, id), prepareData({ ...post, id }, id), { merge: true });
  }

  async updateNewsFeedPost(
    orgId: string,
    postId: string,
    updates: Partial<NewsFeedPost>,
    projectId?: string
  ): Promise<void> {
    const path = this.getNewsFeedPath(orgId, projectId);
    await updateDoc(doc(this.db, path, postId), {
      ...updates,
      updatedAt: serverTimestamp(),
      edited: true,
    });
  }

  async deleteNewsFeedPost(orgId: string, postId: string, projectId?: string): Promise<void> {
    const path = this.getNewsFeedPath(orgId, projectId);
    await this.tombstoneEntity(`${path}/${postId}`);
  }

  async addCommentToNewsFeedPost(
    orgId: string,
    postId: string,
    comment: NewsFeedComment,
    projectId?: string
  ): Promise<void> {
    const path = this.getNewsFeedPath(orgId, projectId);
    const docRef = doc(this.db, path, postId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const postData = snap.data() as NewsFeedPost;
    const existing = postData.postComments || [];
    await updateDoc(docRef, {
      postComments: [...existing, comment],
      updatedAt: serverTimestamp(),
    });
  }

  async addReactionToNewsFeedPost(
    orgId: string,
    postId: string,
    userId: string,
    emoji: string,
    projectId?: string
  ): Promise<void> {
    const path = this.getNewsFeedPath(orgId, projectId);
    const docRef = doc(this.db, path, postId);
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const postData = snap.data() as NewsFeedPost;
    const reactions = postData.reactions || [];
    const exists = reactions.find((r) => r.userId === userId && r.emoji === emoji);
    const nextReactions = exists
      ? reactions.filter((r) => !(r.userId === userId && r.emoji === emoji))
      : [...reactions, { userId, emoji }];
    await updateDoc(docRef, { reactions: nextReactions, updatedAt: serverTimestamp() });
  }

  // =========================================================
  // PROVIDER CONTRACT
  // =========================================================

  getProviderName(): string { return "Firestore"; }
  async isReady(): Promise<boolean> { return this.ready; }
  saveState(): void {}
  async loadStateAll(): Promise<AppState> { return this.loadState("global"); }
  async clearAllData(): Promise<void> { throw new Error("Hard delete disabled."); }
}
